import { Router, watchBusy } from "@frui.ts/screens";
import { computed } from "mobx";
import { Modules } from "config";
import LocalizationService from "services/localizationService";
import ConfirmationService from "services/confirmationService";
import EventBus from "services/eventBus";
import NotificationService from "services/notificationService";
import SecurityService from "services/securityService";
import EnforcementUserRepository from "repositories/enforcementUserRepository";
import ListViewModel from "../../listViewModel";
import EnforcementUserCreateViewModel from "./enforcementUserCreateViewModel";
import EnforcementUserDetailViewModel from "./enforcementUserDetailViewModel";
import { UserType } from "entities/userType";
import EnforcementTabletUserSearchRequest, {
  EnforcementUserSearchValidationRules,
} from "entities/enforcementTabletUserSearchRequest";
import type { PropertyName } from "@frui.ts/helpers";
import { bound } from "@frui.ts/helpers";
import OptionsService from "services/optionsService";
import type EnforcementTabletUserResponse from "entities/enforcementTabletUserResponse";
import { attachAutomaticValidator, hideValidationErrors } from "@frui.ts/validation";
import EnumService from "services/enumService";

const navigationName = "users";

@Router.registerRoute({
  name: Router.Self,
  route: navigationName,
  children: [EnforcementUserDetailViewModel, EnforcementUserCreateViewModel],
})
export default class EnforcementUsersViewModel extends ListViewModel<
  EnforcementTabletUserResponse,
  EnforcementUserDetailViewModel,
  EnforcementUserCreateViewModel
> {
  readonly modelName = "enforcementUser";
  protected abortController?: AbortController;

  constructor(
    protected createViewModelFactory: () => EnforcementUserCreateViewModel,
    protected detailViewModelFactory: ReturnType<typeof EnforcementUserDetailViewModel.Factory>,
    protected repository: EnforcementUserRepository,
    protected optionsService: OptionsService,
    protected localizationService: LocalizationService,
    protected notificationService: NotificationService,
    protected confirmationService: ConfirmationService,
    protected securityService: SecurityService,
    protected eventBus: EventBus,
    protected enumService: EnumService
  ) {
    super();
    this.navigationName = navigationName;
  }

  @computed
  get name() {
    return this.translate("name");
  }

  @computed
  get enforcementUserTypeOptions() {
    return this.optionsService.internalEnumOptions(UserType);
  }

  @computed
  get booleanOptions() {
    return [
      { id: true, label: this.localizationService.translateGeneral("boolean.true") },
      { id: false, label: this.localizationService.translateGeneral("boolean.false") },
    ];
  }

  protected createFilter(): EnforcementTabletUserSearchRequest {
    return attachAutomaticValidator(new EnforcementTabletUserSearchRequest(), EnforcementUserSearchValidationRules);
  }

  @bound
  @watchBusy
  public async loadData() {
    if (this.filter.code) {
      this.filter.code = String(this.filter.code).trim();
    }
    if (this.filter.id) {
      this.filter.id = String(this.filter.id).trim();
    }
    this.abortController = new AbortController();

    const result = await this.repository.getAll(this.filter, this.pagingFilter, this.abortController?.signal);
    this.abortController = undefined;

    const items = result.items.map(item => this.enumService.proxyObjectEnums(item));
    this.setData([
      items,
      {
        limit: result.metadata.limit,
        offset: result.metadata.offset,
        totalItems: result.metadata.total,
      },
    ]);
  }

  protected resetFilterValues(filter: EnforcementTabletUserSearchRequest) {
    super.resetFilterValues(filter);
    hideValidationErrors(this.filter);

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = undefined;
    }
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.enforcement, `enforcementUsers.${code}`, size);
  }

  translateUserType(userType: UserType) {
    return this.localizationService.translateEnum("userType", userType);
  }

  translateFilterAttribute(attribute: PropertyName<EnforcementTabletUserSearchRequest>, size?: number) {
    return this.localizationService.translateAttribute("enforcementTabletUserSearchRequest", attribute, size);
  }
}
